/**
 * Plays a session recording back into the real client, in place of its WebSocket.
 *
 * A recording (server/src/admin/session_recordings.ts) is every message the server sent one player,
 * each stamped with when it went out. The client is a renderer of exactly that stream, so handing it
 * the same messages at the same moments redraws what the player saw: the client's own handshake,
 * netcode, interpolation and rendering all run as they did, and nothing here knows a single message
 * type. Anything the client sends goes nowhere -- there is no server on the other end.
 *
 * Time is VIRTUAL: `now()` is the recording's own clock (its start, plus how far playback has got),
 * and replay mode installs it as Date.now before the client is built. Server timestamps in the
 * stream (a downed player's get-up time, a held speech bubble, a countdown) are therefore compared
 * against the time they were sent at, exactly as during the session, and pausing or playing at 8x is
 * just the clock running at a different rate.
 *
 * Frames are decoded as playback needs them, a few thousand ahead, so an hour-long recording never
 * sits in memory as one string.
 */

export interface RecordingHeader {
    v: number
    id: string
    startTs: number
    contentRevision?: string
}

export interface RecordedFrame {
    /** Milliseconds since the recording started. */
    t: number
    /** "s": the server sent it to the player. "c": the player's client sent it to the server. */
    direction: "s" | "c"
    text: string
}

export interface ReplayStatus {
    /** Playback position, ms since the recording started. */
    t: number
    /** The latest frame decoded so far: the whole length, once `complete`. */
    lastT: number
    complete: boolean
    playing: boolean
    speed: number
    seeking: boolean
    ended: boolean
}

/** One recorded line, or undefined for the header, a blank or a line cut off mid-write. */
export const parseRecordingLine = (line: string): RecordedFrame | undefined => {
    const first = line.indexOf("\t")
    if (first < 1 || line.charCodeAt(0) === 35 /* # */) return undefined
    const second = line.indexOf("\t", first + 1)
    if (second < 0) return undefined
    const t = Number(line.slice(0, first))
    const direction = line.slice(first + 1, second)
    if (!Number.isFinite(t) || (direction !== "s" && direction !== "c")) return undefined
    return { t, direction, text: line.slice(second + 1) }
}

export const parseRecordingHeader = (line: string | undefined): RecordingHeader | undefined => {
    if (!line || !line.startsWith("#")) return undefined
    try {
        const header = JSON.parse(line.slice(1))
        return typeof header?.startTs === "number" ? header : undefined
    } catch {
        return undefined
    }
}

/**
 * The lines of a gzipped recording, decompressed as they are pulled. A recording of a session that
 * is still going has no gzip trailer yet, and the browser's decompressor says so by throwing once it
 * runs out: that is the end of what there is, not an error.
 */
export async function* recordingLines(gzip: ArrayBuffer | Blob): AsyncGenerator<string> {
    const blob = gzip instanceof Blob ? gzip : new Blob([gzip])
    // Typed loosely: the DOM lib this package builds against predates the streams' pipe methods.
    const scope = globalThis as any
    const bytes: any = blob.stream()
    const stream: ReadableStream<string> = bytes.pipeThrough(new scope.DecompressionStream("gzip")).pipeThrough(new scope.TextDecoderStream())
    const reader = stream.getReader()
    let carry = ""
    try {
        while (true) {
            const { value, done } = await reader.read()
            if (done) break
            const lines = (carry + value).split("\n")
            carry = lines.pop() || ""
            for (const line of lines) yield line
        }
    } catch {
        // Ran off the end of a recording still being written. Whatever decoded is good.
    }
    // A last line with no newline was cut off mid-write; parseRecordingLine will not take a half.
    if (carry) yield carry
}

/** How far ahead to decode, in frames. */
const DECODE_LOW_WATER = 1500
const DECODE_HIGH_WATER = 4000
/** A frame that took longer than this (the tab was hidden) does not fast-forward the replay. */
const MAX_FRAME_MS = 250
/** How much recorded time a single drawn frame may cover while seeking. */
const SEEK_STEP_MS = 4000

/** WebSocket-shaped: the fields and methods Client.connectImpl touches. */
export class ReplaySocket {
    readyState = 0
    onopen?: () => void
    onmessage?: (event: { data: string }) => void
    onclose?: () => void
    onerror?: (error: any) => void
    /** What the recorded player's client sent, as of now in the recording ("c" frames). */
    onPlayerSent?: (text: string) => void

    header?: RecordingHeader
    private lines: AsyncIterator<string>
    private frames: RecordedFrame[] = []
    private head = 0
    private decoding = false
    private exhausted = false
    private lastT = 0
    private elapsed = 0
    private playing = true
    private speed = 1
    private seekTarget?: number
    private ended = false

    constructor(lines: AsyncIterable<string>) {
        this.lines = lines[Symbol.asyncIterator]()
    }

    /** Read the header and the first frames. Resolves with the header, rejects on a non-recording. */
    open = async (): Promise<RecordingHeader> => {
        const first = await this.lines.next()
        const header = parseRecordingHeader(first.done ? undefined : first.value)
        if (!header) throw new Error("not a session recording")
        this.header = header
        await this.decode()
        return header
    }

    /** The recording's clock: what Date.now() said on the server when this frame was sent. */
    now = (): number => (this.header?.startTs ?? 0) + this.elapsed

    /** The socket is "connected" once the client has wired its handlers on (Client.connectReplay). */
    start = () => {
        this.readyState = 1
        this.onopen?.()
    }

    /**
     * Once per drawn frame, before the client's own update: move the clock on by `realDtMs` (at the
     * playback speed) and deliver every frame up to it.
     */
    advance = (realDtMs: number) => {
        if (this.readyState !== 1) return
        let target = this.elapsed
        if (this.seekTarget !== undefined) {
            target = Math.min(this.seekTarget, this.elapsed + SEEK_STEP_MS)
        } else if (this.playing && !this.ended) {
            target = this.elapsed + Math.min(Math.max(realDtMs, 0), MAX_FRAME_MS) * this.speed
        }

        while (true) {
            if (this.head >= this.frames.length) {
                if (!this.exhausted) {
                    // Decoding has fallen behind playback: hold the clock where the frames stop.
                    target = Math.min(target, this.lastT)
                }
                break
            }
            const next = this.frames[this.head]
            if (next.t > target) break
            this.head++
            if (next.direction === "s") this.onmessage?.({ data: next.text })
            else this.onPlayerSent?.(next.text)
        }
        this.elapsed = Math.max(this.elapsed, target)
        if (this.seekTarget !== undefined && this.elapsed >= this.seekTarget) this.seekTarget = undefined
        if (this.head >= this.frames.length && this.exhausted) {
            this.ended = true
            this.seekTarget = undefined
        }
        this.compact()
        if (this.frames.length - this.head < DECODE_LOW_WATER) void this.decode()
    }

    play = () => {
        this.playing = true
    }

    pause = () => {
        this.playing = false
    }

    setSpeed = (speed: number) => {
        if (Number.isFinite(speed) && speed > 0) this.speed = Math.min(speed, 64)
    }

    /**
     * Fast-forward to `t` (ms since the start), a few seconds of recording per drawn frame so the
     * client keeps up. Going BACK is not possible: the stream is deltas, so the only way to an earlier
     * moment is to start again from the top (replay mode reloads for that).
     */
    seekTo = (t: number) => {
        if (Number.isFinite(t) && t > this.elapsed) this.seekTarget = t
    }

    status = (): ReplayStatus => ({
        t: this.elapsed,
        lastT: this.lastT,
        complete: this.exhausted,
        playing: this.playing,
        speed: this.speed,
        seeking: this.seekTarget !== undefined,
        ended: this.ended,
    })

    /** A replay answers nothing: there is no server to hear it. */
    send = (_text: string) => undefined

    close = () => {
        if (this.readyState === 3) return
        this.readyState = 3
        this.onclose?.()
    }

    private decode = async () => {
        if (this.decoding || this.exhausted) return
        this.decoding = true
        try {
            while (this.frames.length - this.head < DECODE_HIGH_WATER) {
                const next = await this.lines.next()
                if (next.done) {
                    this.exhausted = true
                    break
                }
                const frame = parseRecordingLine(next.value)
                if (!frame) continue
                this.frames.push(frame)
                this.lastT = Math.max(this.lastT, frame.t)
            }
        } catch (error) {
            this.exhausted = true
            this.onerror?.(error)
        } finally {
            this.decoding = false
        }
    }

    /** Drop delivered frames now and then, rather than shifting the array once per frame. */
    private compact() {
        if (this.head > 2000) {
            this.frames = this.frames.slice(this.head)
            this.head = 0
        }
    }
}
